import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import { Box } from '@mui/material';
import React from 'react';
import { styled } from '@mui/material/styles';
import { configureAmplify } from 'shared/components/auth/utils';
import { pools } from 'shared/components/auth/config';
import { Site } from 'shared/hooks/use-site-detection.types';
import { useSignInEmailFromDom } from 'shared/components/auth/my-authenticator/use-sign-in-email-from-dom';
import { OgpSsoSection } from 'shared/components/auth/my-authenticator/ogp-sso-section';
import { shouldShowOgpSsoSection } from 'shared/components/auth/my-authenticator/ogp-sso-visibility';
import { useGetLoginConfig } from 'shared/hooks/query-hooks/use-get-login-config';
import { useSiteDetection } from 'shared/hooks/use-site-detection';
import { useInjectForgotPassword } from 'shared/components/auth/my-authenticator/use-inject-forgot-password';
import { SignInFooter } from './ogp-sign-in-footer';
import { useCheckCompanyHasSsoEnforced } from './use-check-company-has-sso-enforced';
import { WIDE_LOGO_PATH } from '../../../../config';

export const MyAuthenticator = () => {
  const { site } = useSiteDetection();
  const { checkSsoEnforcement } = useCheckCompanyHasSsoEnforced();

  const loginConfigQuery = useGetLoginConfig();
  const allowOGPSSO =
    loginConfigQuery.status === 'success' ? loginConfigQuery.data.allowOGPSSO : false;
  const formFields: Exclude<React.ComponentProps<typeof Authenticator>['formFields'], undefined> = {
    signIn: {
      username: {
        labelHidden: false,
        placeholder: '',
        isRequired: true,
        type: 'email',
      },
      password: {
        labelHidden: false,
        isRequired: true,
        placeholder: '',
      },
    },
    resetPassword: {
      username: {
        labelHidden: false,
        isRequired: true,
        placeholder: '',
      },
    },
    confirmResetPassword: {
      confirmation_code: {
        labelHidden: false,
        isRequired: true,
        placeholder: '',
      },
      password: {
        labelHidden: false,
        isRequired: true,
        placeholder: '',
      },
      confirm_password: {
        labelHidden: false,
        isRequired: true,
        placeholder: '',
      },
    },
  };

  return (
    <StyledAuthenticator
      services={{
        handleSignIn: async (args) => {
          configureAmplify(pools[site]);
          await checkSsoEnforcement(args);
          return await Auth.signIn(args);
        },
      }}
      components={{
        Header: AuthenticatorHeader,
        ResetPassword: {
          Header: ResetPasswordHeader,
        },
        SignIn: {
          Footer: () => <SignInFooterContent site={site} allowOGPSSO={allowOGPSSO} />,
        },
      }}
      formFields={formFields}
      hideSignUp
    />
  );
};

function AuthenticatorHeader() {
  return <Logo src={WIDE_LOGO_PATH} alt="Logo" />;
}

function ResetPasswordHeader() {
  return <strong style={{ display: 'block' }}>Wachtwoord opnieuw instellen</strong>;
}

function SignInFooterContent({ site, allowOGPSSO }: { site: Site; allowOGPSSO: boolean }) {
  const showOgpSso = shouldShowOgpSsoSection(site, allowOGPSSO);

  const { toResetPassword } = useAuthenticator();
  const signInEmail = useSignInEmailFromDom(showOgpSso);

  useInjectForgotPassword(toResetPassword);

  return (
    <SignInFooterWrapper>
      <OgpSsoSection visible={showOgpSso} />
      {site !== 'hq' && <SignInFooter />}
    </SignInFooterWrapper>
  );
}

const SignInFooterWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

const Logo = styled('img')(() => ({
  width: '100%',
  marginBottom: '75px',
  display: 'block',
}));

const StyledAuthenticator = styled(Authenticator)(({ theme }) => ({
  '.amplify-alert--error': {
    textWrap: 'nowrap',
  },
  '.amplify-label': {
    marginBottom: theme.spacing(2),
  },
  '.amplify-input': {
    padding: theme.spacing(1, 2),
    border: `1px solid ${theme.palette.lwPrimaryAccentLight[40]}`,
    borderRadius: '8px !important',
    height: '48px',
  },
  '.amplify-button': {
    padding: theme.spacing(3, 3),
    borderRadius: '28px',
  },
  '.forgot-password-injected': {
    color: theme.palette.lwPrimaryAccentLight[100],
    fontSize: '14px',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
}));
